import * as React from "react";

import { Badge, STATUS_TONES, StatusBadge } from "Components/ui";
import { Flag } from "Components/ui/icons";
import {
  BillingMode,
  BillingStatus,
  Enforcement,
  InvoiceStatus,
  PaymentStatus,
  ScheduleStatus,
} from "api/billing";

// Billing's status chips over the kit's StatusBadge, which owns every status label and tone
// (Components/ui/StatusBadge.tsx), so billing reads the same as the rest of SAP.

/** Label and description for each derived client billing state. */
export const BILLING_STATUS_META = STATUS_TONES.billing;

export const BillingStatusChip: React.FC<{
  status: BillingStatus;
  size?: "sm" | "md";
  className?: string;
}> = ({ status, size, className }) => (
  <StatusBadge domain="billing" status={status} size={size} className={className} />
);

export const InvoiceStatusChip: React.FC<{
  status: InvoiceStatus;
  needsReview?: boolean;
  overdue?: boolean;
}> = ({ status, needsReview = false, overdue = false }) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    <StatusBadge
      domain="invoice"
      status={status}
      className={status === "void" ? "line-through" : undefined}
    />
    {overdue && <StatusBadge domain="invoice" status="overdue" />}
    {needsReview && <NeedsReviewFlag />}
  </span>
);

export const NeedsReviewFlag: React.FC<{ note?: string | null }> = ({ note }) => (
  <Badge tone="warning" title={note || "Needs review"}>
    <Flag className="h-3 w-3" aria-hidden="true" />
    Review
  </Badge>
);

export const ScheduleStatusChip: React.FC<{ status: ScheduleStatus }> = ({ status }) => (
  <StatusBadge domain="schedule" status={status} />
);

export const PaymentStatusChip: React.FC<{ status: PaymentStatus }> = ({ status }) => (
  <StatusBadge
    domain="payment"
    status={status}
    className={status === "reversed" ? "line-through" : undefined}
  />
);

export const ModeChip: React.FC<{ mode: BillingMode }> = ({ mode }) => (
  <StatusBadge domain="billingMode" status={mode} />
);

export const ENFORCEMENT_LABELS: Record<Enforcement, string> = {
  off: "Enforcement off",
  warn_only: "Warn only",
  read_only: "Warn, then read-only",
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  bank_transfer: "Bank transfer",
  upi: "UPI",
  cheque: "Cheque",
  cash: "Cash",
  other: "Other",
  gateway: "Online",
};
